import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { format } from 'date-fns';
import { targetApi, ccApi } from '../services/api';
import { useTargetStore } from '../store/targetStore';
import { usePlatoonStore } from '../store/platoonStore';
import { useRealtimeStore } from '../store/realtimeStore';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import { distanceToGeometry, formatDistance, formatEta } from '../lib/geo';
import {
  TARGET_STATUS_META,
  TARGET_TYPE_META,
  TARGET_PROXIMITY_M,
  type Target,
} from '../types/domain';
import SheetDialog from '../components/responsive/SheetDialog';

function StatusChip({ status }: { status: Target['status'] }) {
  const m = TARGET_STATUS_META[status];
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ color: m?.color, backgroundColor: `${m?.color}22`, border: `1px solid ${m?.color}55` }}>
      {m?.label ?? status}
    </span>
  );
}

export default function TargetsPage() {
  const { user } = useAuthStore();
  const { isHQ } = useUnitStore();
  const userIsHQ = isHQ(user?.unitId ?? null);

  const { targets, fetchTargets, includeExpired, setIncludeExpired, applyStatus } = useTargetStore();
  const { platoons, fetchPlatoons } = usePlatoonStore();
  const { locations } = useRealtimeStore();
  const [assignFor, setAssignFor] = useState<Target | null>(null);

  useEffect(() => {
    fetchTargets();
    fetchPlatoons();
    const t = setInterval(() => {
      fetchTargets();
      fetchPlatoons();
    }, 5000);
    return () => clearInterval(t);
  }, [fetchTargets, fetchPlatoons, includeExpired]);

  // platoonId -> latest live location
  const locByPlatoon = useMemo(() => {
    const m = new Map<string, { lat: number; lon: number; speed?: number }>();
    for (const loc of locations.values()) {
      const pid = loc.platoonId || loc.platoon?.id;
      if (pid) m.set(pid, { lat: loc.latitude, lon: loc.longitude, speed: loc.speed });
    }
    return m;
  }, [locations]);

  const scoreboardQ = useQuery({
    queryKey: ['scoreboard'],
    queryFn: async () => (await ccApi.getScoreboard()).data,
    retry: false,
    refetchInterval: 10000,
  });

  const localScore = useMemo(() => {
    let eliminated = 0, failed = 0, remaining = 0;
    for (const t of targets) {
      if (t.status === 'ELIMINATED') eliminated++;
      else if (t.status === 'FAILED') failed++;
      else if (['ACTIVE', 'ASSIGNED', 'EXECUTING', 'INACTIVE'].includes(t.status)) remaining++;
    }
    return { eliminated, failed, remaining };
  }, [targets]);
  // Scoreboard shape is `{ overall, byUnit, byPlatoon }` (§19.3); read overall, fall back to local.
  const overall = scoreboardQ.data?.overall as
    | { eliminated?: number; failed?: number; remaining?: number }
    | undefined;
  const score = {
    eliminated: overall?.eliminated ?? localScore.eliminated,
    failed: overall?.failed ?? localScore.failed,
    remaining: overall?.remaining ?? localScore.remaining,
  };

  const adjudicate = async (t: Target, success: boolean) => {
    // Optimistic: success → ELIMINATED; fail → FAILED if past closingTime, else ASSIGNED (§17).
    const pastClosing = !!t.closingTime && new Date(t.closingTime).getTime() <= Date.now();
    applyStatus(t.id, success ? 'ELIMINATED' : pastClosing ? 'FAILED' : 'ASSIGNED');
    try {
      const res = await targetApi.adjudicate(t.id, { success });
      // Server is authoritative on the resulting status (ASSIGNED re-executable vs terminal FAILED).
      const serverStatus = res.data?.target?.status as Target['status'] | undefined;
      if (serverStatus) applyStatus(t.id, serverStatus);
      toast.success(success ? `${t.name} eliminated` : `${t.name} not successful`);
      fetchTargets();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Adjudication failed');
      fetchTargets();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg font-display font-bold text-gray-100">Target Board</h1>
          <p className="text-gray-500 text-[11px]">Kill-chain · assignment · adjudication</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-400 min-h-11 touch-manipulation cursor-pointer">
          <input type="checkbox" className="w-5 h-5 lg:w-4 lg:h-4" checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} aria-label="Show expired targets" />
          Show expired
        </label>
      </div>

      {/* Scoreboard */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-xl border border-green-500/30 bg-command-panel text-center">
          <div className="text-2xl font-display font-bold text-green-400">{score.eliminated ?? localScore.eliminated}</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide">Eliminated</div>
        </div>
        <div className="p-3 rounded-xl border border-red-500/30 bg-command-panel text-center">
          <div className="text-2xl font-display font-bold text-red-400">{score.failed ?? localScore.failed}</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide">Failed</div>
        </div>
        <div className="p-3 rounded-xl border border-command-border bg-command-panel text-center">
          <div className="text-2xl font-display font-bold text-gray-200">{score.remaining ?? localScore.remaining}</div>
          <div className="text-[10px] text-gray-400 uppercase tracking-wide">Remaining</div>
        </div>
      </div>

      {/* Target table */}
      <div className="rounded-xl border border-command-border bg-command-panel overflow-hidden">
        <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
                <th className="text-left py-2 px-3">Target</th>
                <th className="text-left py-2 px-3">Type / Op</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Window</th>
                <th className="text-left py-2 px-3">Assigned · distance/ETA</th>
                {userIsHQ && <th className="text-right py-2 px-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {targets.map((t) => {
                const meta = TARGET_TYPE_META[t.type];
                const awaiting = t.status === 'EXECUTING';
                return (
                  <tr key={t.id} className="border-b border-command-border/60 align-top">
                    <td className="py-2 px-3">
                      <div className="font-semibold text-gray-100 flex items-center gap-1">
                        <span>{meta?.icon}</span>{t.name}
                      </div>
                      <div className="text-[10px] text-gray-500">enemy {t.enemyManpower ?? '—'}</div>
                    </td>
                    <td className="py-2 px-3 text-gray-300">
                      {meta?.label}
                      <div className="text-[10px] text-command-accent">{meta?.operation}</div>
                    </td>
                    <td className="py-2 px-3"><StatusChip status={t.status} /></td>
                    <td className="py-2 px-3 text-[11px] text-gray-400">
                      {t.openingTime ? format(new Date(t.openingTime), 'dd MMM HH:mm') : '—'}
                      {' → '}
                      {t.closingTime ? format(new Date(t.closingTime), 'HH:mm') : '—'}
                    </td>
                    <td className="py-2 px-3">
                      {(t.assignments ?? []).length === 0 ? (
                        <span className="text-[11px] text-gray-600">unassigned</span>
                      ) : (
                        <div className="space-y-0.5">
                          {(t.assignments ?? []).map((a) => {
                            const loc = locByPlatoon.get(a.platoonId);
                            const dist = loc ? distanceToGeometry(loc, t) : null;
                            const near = dist != null && dist <= TARGET_PROXIMITY_M;
                            return (
                              <div key={a.id} className={clsx('text-[11px]', near ? 'text-green-400 font-semibold' : 'text-gray-300')}>
                                {a.callsign || a.platoon?.callsign || a.platoonId?.slice(0, 6) || 'plt'}
                                {dist != null ? ` · ${formatDistance(dist)} · ${formatEta(dist, loc?.speed)}` : ' · no fix'}
                                {near && ' · IN RANGE'}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    {userIsHQ && (
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <button type="button" onClick={() => setAssignFor(t)} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-command-accent touch-manipulation">Assign</button>
                        {awaiting && (
                          <>
                            <button type="button" onClick={() => adjudicate(t, true)} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-green-400 touch-manipulation">✓ Success</button>
                            <button type="button" onClick={() => adjudicate(t, false)} className="inline-flex items-center min-h-11 lg:min-h-0 px-2 text-xs text-red-400 touch-manipulation">✗ Fail</button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {targets.length === 0 && (
                <tr><td colSpan={userIsHQ ? 6 : 5} className="py-8 text-center text-gray-500 text-sm">No targets. Create them in the Location Studio.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {assignFor && (
        <AssignDialog
          target={assignFor}
          platoons={platoons.filter((p) => !assignFor.unitId || p.unitId === assignFor.unitId)}
          onClose={() => setAssignFor(null)}
          onAssigned={() => {
            setAssignFor(null);
            fetchTargets();
          }}
        />
      )}
    </div>
  );
}

function AssignDialog({
  target,
  platoons,
  onClose,
  onAssigned,
}: {
  target: Target;
  platoons: { id: string; callsign: string }[];
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(
    (target.assignments ?? []).map((a) => a.platoonId).filter(Boolean) as string[]
  );
  const [busy, setBusy] = useState(false);
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one platoon');
      return;
    }
    setBusy(true);
    try {
      await targetApi.assign(target.id, { platoonIds: selected });
      toast.success(`Assigned ${selected.length} platoon(s) to ${target.name}`);
      onAssigned();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Assign failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SheetDialog
      open
      onClose={onClose}
      widthClass="sm:max-w-md"
      title={
        <div>
          Assign platoons
          <p className="text-[11px] font-normal text-gray-500">
            {target.name} · {TARGET_TYPE_META[target.type]?.operation}
          </p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 sm:py-1.5 rounded-lg text-sm text-gray-400 border border-command-border touch-manipulation">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="px-5 py-2.5 sm:py-1.5 rounded-lg text-sm font-semibold bg-command-accent text-command-bg disabled:opacity-50 touch-manipulation">
            {busy ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      }
    >
      <div className="space-y-1">
        {platoons.map((p) => (
          <label key={p.id} className="flex items-center gap-3 px-2 py-1.5 min-h-11 rounded hover:bg-command-bg/50 cursor-pointer touch-manipulation">
            <input type="checkbox" className="w-5 h-5 sm:w-4 sm:h-4" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} aria-label={`Assign ${p.callsign}`} />
            <span className="text-sm text-gray-200">{p.callsign}</span>
          </label>
        ))}
        {platoons.length === 0 && <p className="text-xs text-gray-500 py-4 text-center">No platoons in scope.</p>}
      </div>
    </SheetDialog>
  );
}
